import {
  HAS_PERMISSION,
  HAS_SOME_DEVELOPMENT_PERMISSIONS,
  HAS_VIEW_DEVELOPMENT_PERMISSION,
  HAS_VIEW_RESOURCES_PERMISSION,
  HAS_SIMPLIFIED_NAVIGATION,
  IS_COMPANY_GROUP_ADMIN,
} from '@homeviews/users'
import type { NavItem, User } from '@homeviews/models'
import { isEngageUser } from '@homeviews/store'
import {
  NAVIGATION,
  COMPANY_GROUP_ADMIN_VISIBLE_SLUGS,
  LEGACY_NAV_IDENTIFIERS,
} from '../constants/navigation'

export default function filterMainMenu(
  user: User | null | undefined,
  menu: NavItem[],
): NavItem[] {
  // Until the user has been resolved we cannot know which items are allowed.
  // Render nothing rather than the permissive default, otherwise restricted
  // users briefly see the full menu before it collapses to their allowed items.
  if (!user) {
    return []
  }

  // CompanyGroupAdmin users get a restricted, read-only set of menu items.
  if (IS_COMPANY_GROUP_ADMIN(user)) {
    return menu.filter((item: NavItem) =>
      COMPANY_GROUP_ADMIN_VISIBLE_SLUGS.includes(item.slug),
    )
  }

  const isEngagePackage = Boolean(isEngageUser)
  const simplifiedNavigation = HAS_SIMPLIFIED_NAVIGATION(user)
  return menu.filter((item: NavItem) => {
    // Users flagged in Athena get the slimmed down navigation (RVW-1718).
    if (
      simplifiedNavigation &&
      LEGACY_NAV_IDENTIFIERS.has(item.slug ?? item.title ?? '')
    ) {
      return false
    }

    if (
      NAVIGATION.companies.slug === item.slug &&
      !HAS_PERMISSION(user, 'EDIT_COMPANY_PAGE', 'global')
    ) {
      return false
    } else if (
      NAVIGATION.users.slug === item.slug &&
      !HAS_PERMISSION(user, 'EDIT_PERMISSIONS', 'global')
    ) {
      return false
    }

    const canCreateCompanyWidgets = HAS_PERMISSION(
      user,
      'CREATE_WIDGETS',
      'global',
    )

    if (NAVIGATION.widgets.slug === item.slug) {
      let canCreateDevWidgets = false
      try {
        Object.keys(user.development_permissions).map((key: string) => {
          if (
            HAS_PERMISSION(user, 'CREATE_WIDGETS', 'development', {
              devId: key,
            })
          ) {
            canCreateDevWidgets = true
          }
        })
      } catch {
        //
      }
      if (!canCreateCompanyWidgets && !canCreateDevWidgets) {
        return false
      }
    }

    // Only show MenuItem for Resident Outreach if User has at least
    // 1 Development with UNDERTAKE_RESIDENT_OUTREACH_REQUESTS permission.
    if (NAVIGATION.outreachHome.slug === item.slug) {
      const permission = 'UNDERTAKE_RESIDENT_OUTREACH_REQUESTS'
      return HAS_SOME_DEVELOPMENT_PERMISSIONS(user, [permission])
    }

    // Only show MenuItem for Analytics if User has at least
    // 1 Development with VIEW_DEVELOPMENT permission.
    if (NAVIGATION.analytics.slug === item.slug) {
      return HAS_SOME_DEVELOPMENT_PERMISSIONS(user, ['VIEW_DEVELOPMENT'])
    }

    if (
      NAVIGATION.dashboard.slug === item.slug &&
      !HAS_VIEW_DEVELOPMENT_PERMISSION(user)
    ) {
      return isEngagePackage
    }

    if (
      NAVIGATION.resources.slug === item.slug &&
      !HAS_VIEW_RESOURCES_PERMISSION(user)
    ) {
      return false
    }

    return item
  })
}
